import type {
  MetricCategory,
  ReportMetric,
  ReportPreset,
  ReportDefinition,
  RevenueBasis,
} from './types';

export const PRACTITIONER_METRIC_IDS = {
  revenue: 'revenue',
  commission_amount: 'commission_amount',
  commission_percentage: 'commission_percentage',
  average_revenue_per_appointment: 'average_revenue_per_appointment',
  billable_appointments: 'billable_appointments',
  discounts: 'discounts',
  refunds: 'refunds',
  product_sales: 'product_sales',
  payment_method_split: 'payment_method_split',
  outstanding_amount: 'outstanding_amount',
  paid_amount: 'paid_amount',
  appointment_count: 'appointment_count',
  completed_appointments: 'completed_appointments',
  cancelled_appointments: 'cancelled_appointments',
  no_shows: 'no_shows',
  average_appointment_length: 'average_appointment_length',
  utilisation_rate: 'utilisation_rate',
  average_appointments_per_working_day: 'average_appointments_per_working_day',
  unique_patients: 'unique_patients',
  new_patients: 'new_patients',
  returning_patients: 'returning_patients',
  retention_rate: 'retention_rate',
  average_visits_per_patient: 'average_visits_per_patient',
  patients_seen_per_practitioner: 'patients_seen_per_practitioner',
  new_patient_percentage: 'new_patient_percentage',
  most_common_services: 'most_common_services',
  service_mix: 'service_mix',
  treatments_per_service: 'treatments_per_service',
  revenue_per_service: 'revenue_per_service',
  average_revenue_per_service: 'average_revenue_per_service',
  average_appointments_per_patient_by_service: 'average_appointments_per_patient_by_service',
  revenue_per_working_day: 'revenue_per_working_day',
  revenue_per_appointment: 'revenue_per_appointment',
  revenue_per_practitioner: 'revenue_per_practitioner',
  appointment_completion_rate: 'appointment_completion_rate',
  cancellation_rate: 'cancellation_rate',
  no_show_rate: 'no_show_rate',
  new_patient_acquisition: 'new_patient_acquisition',
  returning_patient_percentage: 'returning_patient_percentage',
} as const;

export type PractitionerMetricId =
  (typeof PRACTITIONER_METRIC_IDS)[keyof typeof PRACTITIONER_METRIC_IDS];

function metric(
  id: PractitionerMetricId,
  category: MetricCategory,
  format: ReportMetric['format'],
  options: Partial<Pick<ReportMetric, 'summary' | 'column' | 'available'>> = {},
): ReportMetric {
  return {
    id,
    labelKey: `reports.metrics.${id}.label`,
    descriptionKey: `reports.metrics.${id}.description`,
    category,
    format,
    available: options.available ?? true,
    summary: options.summary ?? true,
    column: options.column ?? true,
    groupable: true,
    aggregatable: true,
  };
}

/**
 * Only metrics that can be calculated from current Clinio data are marked available.
 * Patient satisfaction / treatment outcomes / questionnaires are omitted — those
 * tables are not a reliable source for practitioner performance columns.
 */
export const PRACTITIONER_METRICS: ReportMetric[] = [
  metric('revenue', 'revenue', 'currency'),
  metric('commission_amount', 'revenue', 'currency'),
  metric('commission_percentage', 'revenue', 'percent', { summary: false }),
  metric('average_revenue_per_appointment', 'revenue', 'currency'),
  metric('billable_appointments', 'revenue', 'integer'),
  metric('discounts', 'revenue', 'currency'),
  metric('refunds', 'revenue', 'currency'),
  metric('product_sales', 'revenue', 'currency'),
  metric('payment_method_split', 'revenue', 'text', { summary: true }),
  metric('outstanding_amount', 'revenue', 'currency'),
  metric('paid_amount', 'revenue', 'currency'),
  metric('appointment_count', 'appointment', 'integer'),
  metric('completed_appointments', 'appointment', 'integer'),
  metric('cancelled_appointments', 'appointment', 'integer'),
  metric('no_shows', 'appointment', 'integer'),
  metric('average_appointment_length', 'appointment', 'decimal', { summary: false }),
  metric('utilisation_rate', 'appointment', 'percent'),
  metric('average_appointments_per_working_day', 'appointment', 'decimal', { summary: false }),
  metric('unique_patients', 'patient', 'integer'),
  metric('new_patients', 'patient', 'integer'),
  metric('returning_patients', 'patient', 'integer'),
  metric('retention_rate', 'patient', 'percent'),
  metric('average_visits_per_patient', 'patient', 'decimal', { summary: false }),
  metric('patients_seen_per_practitioner', 'patient', 'decimal', { summary: false }),
  metric('new_patient_percentage', 'patient', 'percent'),
  metric('most_common_services', 'service', 'text', { summary: false }),
  metric('service_mix', 'service', 'percent', { summary: false }),
  metric('treatments_per_service', 'service', 'integer', { summary: false }),
  metric('revenue_per_service', 'service', 'currency', { summary: false }),
  metric('average_revenue_per_service', 'service', 'currency', { summary: false }),
  metric('average_appointments_per_patient_by_service', 'service', 'decimal', { summary: false }),
  metric('revenue_per_working_day', 'performance', 'currency', { summary: false }),
  metric('revenue_per_appointment', 'performance', 'currency'),
  metric('revenue_per_practitioner', 'performance', 'currency', { summary: false }),
  metric('appointment_completion_rate', 'performance', 'percent'),
  metric('cancellation_rate', 'performance', 'percent'),
  metric('no_show_rate', 'performance', 'percent'),
  metric('new_patient_acquisition', 'performance', 'integer'),
  metric('returning_patient_percentage', 'performance', 'percent'),
];

export const METRIC_BY_ID: Record<string, ReportMetric> = Object.fromEntries(
  PRACTITIONER_METRICS.map((item) => [item.id, item]),
);

export const AVAILABLE_METRICS = PRACTITIONER_METRICS.filter((item) => item.available);

export const METRIC_CATEGORIES: MetricCategory[] = [
  'revenue',
  'appointment',
  'patient',
  'service',
  'performance',
];

export const PRACTITIONER_PRESETS: ReportPreset[] = [
  {
    id: 'practitioner_performance',
    labelKey: 'reports.presets.practitioner_performance.label',
    descriptionKey: 'reports.presets.practitioner_performance.description',
    grouping: 'practitioner',
    metricIds: [
      'revenue',
      'commission_amount',
      'commission_percentage',
      'appointment_count',
      'unique_patients',
    ],
  },
  {
    id: 'commission',
    labelKey: 'reports.presets.commission.label',
    descriptionKey: 'reports.presets.commission.description',
    grouping: 'practitioner_service',
    metricIds: ['revenue', 'commission_amount', 'commission_percentage'],
  },
  {
    id: 'revenue_by_service',
    labelKey: 'reports.presets.revenue_by_service.label',
    descriptionKey: 'reports.presets.revenue_by_service.description',
    grouping: 'service',
    metricIds: [
      'revenue',
      'treatments_per_service',
      'revenue_per_service',
      'average_revenue_per_service',
    ],
  },
  {
    id: 'patient_activity',
    labelKey: 'reports.presets.patient_activity.label',
    descriptionKey: 'reports.presets.patient_activity.description',
    grouping: 'practitioner',
    metricIds: ['unique_patients', 'new_patients', 'returning_patients', 'retention_rate'],
  },
  {
    id: 'activity',
    labelKey: 'reports.presets.activity.label',
    descriptionKey: 'reports.presets.activity.description',
    grouping: 'practitioner_service',
    metricIds: [
      'appointment_count',
      'completed_appointments',
      'cancelled_appointments',
      'no_shows',
      'average_appointment_length',
    ],
  },
];

export const SUPPORTED_REVENUE_BASES: RevenueBasis[] = ['invoiced', 'paid'];

export const PRACTITIONER_REPORT_DEFINITION: ReportDefinition = {
  id: 'practitioner',
  labelKey: 'reports.types.practitioner.label',
  descriptionKey: 'reports.types.practitioner.description',
  metrics: AVAILABLE_METRICS,
  presets: PRACTITIONER_PRESETS,
  groupings: ['practitioner_service', 'service_practitioner', 'practitioner', 'service'],
  revenueBases: SUPPORTED_REVENUE_BASES,
};

export const DEFAULT_PRESET_ID = 'practitioner_performance';

export const DEFAULT_METRIC_IDS: string[] =
  PRACTITIONER_PRESETS.find((preset) => preset.id === DEFAULT_PRESET_ID)?.metricIds ?? [
    'revenue',
    'commission_amount',
    'commission_percentage',
    'appointment_count',
    'unique_patients',
  ];

export function metricsByCategory(category: MetricCategory): ReportMetric[] {
  return AVAILABLE_METRICS.filter((item) => item.category === category);
}
